import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Header } from './components/Header';
import { ImageUploader } from './components/ImageUploader';
import { ImageViewer } from './components/ImageViewer';
import { ControlPanel } from './components/ControlPanel';
import { LandingPage } from './components/LandingPage';
import { TrialModal } from './components/TrialModal';
import { TrialPage } from './components/TrialPage';
import { TermsOfService } from './components/TermsOfService';
import { LoginModal } from './components/LoginModal';
import { PaymentModal } from './components/PaymentModal';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { CommercialTransactionAct } from './components/CommercialTransactionAct';
import { PlanSelectionModal } from './components/PlanSelectionModal';
import AccountLinkModal from './components/AccountLinkModal';
import { GalleryModal } from './components/GalleryModal';
// ShowcaseModalは独立したHTMLページとして実装
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentCancel } from './pages/PaymentCancel';
import { MusicianPage } from './pages/MusicianPage';
import { PortraitPage } from './pages/PortraitPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { generateHeadshot } from './services/geminiService';
import { updateCreditsAndSaveImage, testFirestoreConnection, checkUserPlanManually, getShowcaseItemById } from './services/firebase';
import { OUTFIT_OPTIONS, POSE_OPTIONS } from './constants';
import { getTemplateById } from './constants/templates';
import {
  clearInheritedStyle,
  loadInheritedStyle,
  normalizeInheritedSettings,
  saveInheritedStyle,
  type InheritedStylePayload,
} from './utils/showcaseStyle';

function AppContent() {
  const { user, userPlan, isPaidUser, isLoading, updateUserPlan, forceSyncCredits } = useAuth();
  
  const [showLandingPage, setShowLandingPage] = useState(true);
  
  // デバッグ用：showLandingPageの変更を監視
  useEffect(() => {
    console.log('🔍 showLandingPage changed:', { showLandingPage });
  }, [showLandingPage]);
  
  // TrialPage の状態変数
  const [trialSelectedTemplate, setTrialSelectedTemplate] = useState('studio_white');
  const [trialSelectedOutfit, setTrialSelectedOutfit] = useState('suit_blue');
  const [trialSelectedPose, setTrialSelectedPose] = useState('front');
  const [trialCustomOutfitText, setTrialCustomOutfitText] = useState('');
  const [trialCustomTemplateText, setTrialCustomTemplateText] = useState('');
  
  // 状態変数の定義（初期化順序を修正）
  const [currentStep, setCurrentStep] = useState<'upload' | 'template' | 'generate' | 'result'>('upload');
  
  // URLパラメータをチェックしてLINE内ブラウザからのアクセスを検出
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openExternalBrowser = urlParams.get('openExternalBrowser');
    
    if (openExternalBrowser === '1') {
      // LINE内ブラウザからSafariで開き直された場合
      console.log('✅ Safariで開き直されました');
      
      // URLパラメータをクリーンアップ（履歴を汚さないため）
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  // ログイン状態に応じてTrialModalの表示を制御（自動遷移はしない）
  useEffect(() => {
    if (!isLoading && user) {
      // ログインしている場合はTrialModalを閉じる
      setShowTrialModal(false);
    }
  }, [user, isLoading]);

  // スマホでのスクロール位置が負の値にならないようにする
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 0) {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showCommercialAct, setShowCommercialAct] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPlanSelectionModal, setShowPlanSelectionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAccountLinkModal, setShowAccountLinkModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'standard' | 'pro' | 'pro_max'>('pro');
  const [originalImage, setOriginalImage] = useState<{ data: string; mimeType: string } | null>(null);

  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  // 無料体験の使用回数はFirebaseから取得するため、useStateは不要
  
  // 進行中のリクエストを追跡するためのref（再生成時の競合を防ぐ）
  const currentGenerationIdRef = useRef<number>(0);

  // 画像生成中の進行状況を更新
  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    const steps = [
      { progress: 20, text: '画像を分析中...' },
      { progress: 40, text: 'AIが生成中...' },
      { progress: 60, text: '画像を処理中...' },
      { progress: 80, text: '最終調整中...' },
      { progress: 95, text: '仕上げ中...' }
    ];

    let currentStepIndex = 0;
    let maxProgress = 0;
    
    const interval = setInterval(() => {
      if (currentStepIndex < steps.length) {
        const progress = steps[currentStepIndex].progress;
        setGenerationProgress(progress);
        setGenerationStep(steps[currentStepIndex].text);
        maxProgress = Math.max(maxProgress, progress);
        currentStepIndex++;
      } else {
        // 最後のステップで95%に留める（ただし、タイムアウトを防ぐため最大90秒で100%に戻す）
        if (maxProgress < 95) {
          setGenerationProgress(95);
          maxProgress = 95;
        }
      }
    }, 4000); // 4秒ごとに更新

    // モバイル環境でのタイムアウト保護（90秒後に100%に戻してエラーを表示）
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const timeoutMs = isMobile ? 90000 : 60000;
    
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ 画像生成がタイムアウトしました（プログレスバー保護）');
      setGenerationProgress(100);
      setGenerationStep('タイムアウト...');
    }, timeoutMs);

    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [isGenerating]);

  // States for customization options
  const [selectedOutfitId, setSelectedOutfitId] = useState<string>('suit_blue');
  const [customOutfitText, setCustomOutfitText] = useState<string>('');
  const [customOutfitImage, setCustomOutfitImage] = useState<{ data: string; mimeType: string; } | null>(null);
  const [backgroundOption, setBackgroundOption] = useState<string>('default');
  const [customBackground, setCustomBackground] = useState<string>('');
  const [customBackgroundImage, setCustomBackgroundImage] = useState<{ data: string; mimeType: string; } | null>(null);
  const [selectedPoseId, setSelectedPoseId] = useState<string>('front');
  const [inheritedStyle, setInheritedStyle] = useState<InheritedStylePayload | null>(null);
  
  // 設定をリセットする関数
  const resetSettings = () => {
    setSelectedOutfitId('suit_blue');
    setCustomOutfitText('');
    setCustomOutfitImage(null);
    setBackgroundOption('default');
    setCustomBackground('');
    setCustomBackgroundImage(null);
    setSelectedPoseId('front');
  };

  const applyInheritedStyle = (payload: InheritedStylePayload) => {
    const settings = normalizeInheritedSettings(payload.settings, payload.prompt);
    if (settings.mode === 'musician') {
      // 音楽家モードは MusicianPage 側で処理する
      return false;
    }

    setBackgroundOption(settings.backgroundOption || 'default');
    setSelectedOutfitId(settings.selectedOutfitId || 'suit_blue');
    setSelectedPoseId(settings.selectedPoseId || 'front');
    setCustomBackground(settings.customBackground || '');
    setCustomOutfitText(settings.customOutfitText || '');
    // 他人の参照画像は引き継がない
    setCustomBackgroundImage(null);
    setCustomOutfitImage(null);
    setInheritedStyle({ ...payload, settings });
    setShowLandingPage(false);
    setCurrentStep('upload');
    return true;
  };

  // ショーケースからスタイルを引き継ぐ
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inheritStyle = urlParams.get('inheritStyle');
    const showcaseId = urlParams.get('showcaseId');

    if (inheritStyle !== '1' && !showcaseId) {
      return;
    }

    let cancelled = false;

    const loadStyle = async () => {
      try {
        let payload = loadInheritedStyle();

        if ((!payload || !payload.settings) && showcaseId) {
          const item = await getShowcaseItemById(showcaseId);
          if (item) {
            payload = {
              showcaseId: item.id,
              prompt: item.prompt,
              imageUrl: item.imageUrl,
              settings: item.settings || null,
            };
            saveInheritedStyle(payload);
          }
        }

        if (cancelled || !payload) {
          return;
        }

        const settings = normalizeInheritedSettings(payload.settings, payload.prompt);
        if (settings.mode === 'musician') {
          window.location.replace(`/musician?inheritStyle=1${showcaseId ? `&showcaseId=${encodeURIComponent(showcaseId)}` : ''}`);
          return;
        }

        if (settings.mode === 'portrait') {
          window.location.replace(`/portrait?inheritStyle=1${showcaseId ? `&showcaseId=${encodeURIComponent(showcaseId)}` : ''}`);
          return;
        }

        applyInheritedStyle({ ...payload, settings });

        // URLをきれいにする（履歴を汚さない）
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        console.error('ショーケーススタイルの引き継ぎに失敗:', error);
      }
    };

    loadStyle();

    return () => {
      cancelled = true;
    };
  }, []);

  // 残りクレジット数を取得（creditsRestのみ使用）
  const getCreditsRest = () => {
    if (user && userPlan) {
      const result = userPlan.creditsRest || 0;
      console.log('🔍 App.tsx - getCreditsRest詳細:', {
        creditsRest: result,
        plan: userPlan.plan,
        userId: user.uid
      });
      return result;
    }
    return 0;
  };

  // 無料体験の使用回数を更新（すべてFirebaseに保存）
  const updateFreeTrialUsage = async () => {
    if (user) {
      // ログイン時（匿名含む）はFirebaseに保存
      try {
        const { atomicConsumeCredit } = await import('./services/firebase');
        // 無料プランのクレジット使用量を更新
        const result = await atomicConsumeCredit(user.uid);
        if (result.success) {
        // クレジット使用量を更新しました
      } else {
        // クレジットが不足しています
        }
      } catch (error) {
        console.error('❌ 無料プランのクレジット更新エラー:', error);
      }
    } else {
      // ユーザーが存在しない場合はエラー（通常は発生しない）
      console.error('ユーザーが存在しません');
    }
  };

  // URLパラメータをチェックして表示モードを決定
  useEffect(() => {
    // アプリ使用中（upload以外のステップ）の場合は自動遷移しない
    if (currentStep !== 'upload') {
      console.log('🔍 アプリ使用中のため自動遷移をスキップ:', { currentStep });
      return;
    }

    // ショーケースからスタイル引き継ぎ中はランディングに戻さない
    if (inheritedStyle || loadInheritedStyle()) {
      console.log('🔍 スタイル引き継ぎ中のためランディング遷移をスキップ');
      setShowLandingPage(false);
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const tryParam = urlParams.get('try');
    const inheritStyle = urlParams.get('inheritStyle');
    
    console.log('🔍 URLパラメータ処理:', {
      mode,
      tryParam,
      inheritStyle,
      user: user ? { uid: user.uid } : null,
      isPaidUser,
      userPlan: userPlan?.plan,
      isLoading,
      currentURL: window.location.href,
      searchParams: window.location.search,
      showLandingPage,
      currentStep
    });
    
    // URLパラメータを処理
    if (mode === 'app' || inheritStyle === '1') {
      console.log('🔍 mode=app / inheritStyle でアプリ画面を表示');
      setShowLandingPage(false);
    } else if (tryParam === '1' && !user) {
      // 無料体験は未ログイン時のみ
      console.log('🔍 tryParam=1でTrialModalを表示');
      setShowLandingPage(false);
      setShowTrialModal(true);
    } else if (user) {
      // ログインユーザーの場合
      console.log('🔍 ログインユーザーの表示制御:', {
        isPaidUser,
        userPlanPlan: userPlan?.plan,
        creditsUsed: userPlan?.creditsUsed
      });
      
      if (isPaidUser) {
        // 課金ユーザーも最初はランディングページを表示
        console.log('🔍 課金ユーザー: ランディングページを表示');
        setShowLandingPage(true);
      } else {
        // 無料プランユーザー
        if (userPlan === null) {
          console.log('🔍 userPlanがnullのため処理をスキップ');
          return;
        }
        
        // ログイン済み無料ユーザー
        // 最初はLandingPageを表示（ユーザーが手動でアプリを開始するまで待機）
        console.log('🔍 ログイン済み無料ユーザー: LandingPageを表示');
        setShowLandingPage(true);
      }
    } else {
      // 未ログインの場合はLandingPageを表示
      console.log('🔍 未ログイン: LandingPageを表示');
      setShowLandingPage(true);
    }
  }, [user, isPaidUser, userPlan, isLoading, currentStep, inheritedStyle]);

  const handleStartTrial = async () => {
    try {
      // ログイン必須のため、TrialPageは廃止
      console.log('ログイン必須のため、TrialPageは廃止されました');
      
      // ログイン画面を表示
      setShowLoginModal(true);
    } catch (error) {
      console.error('TrialPage開始エラー:', error);
    }
  };

  const handleStartApp = () => {
    console.log('🔍 handleStartApp called:', { isPaidUser, userPlan: userPlan?.plan });
    console.log('🔍 Before setShowLandingPage(false):', { showLandingPage });
    setShowLandingPage(false);
    console.log('🔍 After setShowLandingPage(false):', { showLandingPage });
    setCurrentStep('upload'); // ステップをリセット
  };

  const handleGoHome = async () => {
    // 状態管理でLandingPageに戻る（ページリロードしない）
    console.log('🔍 handleGoHome: LandingPageに戻ります');
    setShowLandingPage(true);
    setCurrentStep('upload'); // ステップをリセット
    resetSettings(); // 設定をリセット
    setInheritedStyle(null);
    clearInheritedStyle();
  };

  const handleUpgrade = () => {
    if (!user) {
      // 未ログインの場合はログイン画面を表示
      setShowLoginModal(true);
      return;
    }
    // ログイン済みの場合はプラン選択画面を表示
    setShowPlanSelectionModal(true);
  };

  const handleLoginSuccess = () => {
    console.log('🔍 handleLoginSuccess呼び出し:', { currentStep });
    console.log('🔍 handleLoginSuccess - 現在の状態:', { 
      showLoginModal, 
      showAccountLinkModal, 
      showLandingPage,
      user: user?.uid,
      isPaidUser 
    });
    setShowLoginModal(false);
    
    // ログイン成功後は強制的にランディングページに遷移
    console.log('✅ ログイン成功: 強制的にランディングページに遷移します');
    
    // 強制的にページをリロードしてランディングページに遷移
    setTimeout(() => {
      console.log('🔄 ページをリロードしてランディングページに戻ります');
      window.location.href = window.location.origin + window.location.pathname;
    }, 200);
  };

  const handlePlanSelect = (plan: 'standard' | 'pro' | 'pro_max') => {
    setSelectedPlan(plan);
    setShowPlanSelectionModal(false);
    // プラン選択後は決済画面を表示
    setShowPaymentModal(true);
  };

  const handleAccountLinkSuccess = () => {
    console.log('🔍 アカウントリンク成功');
    setShowAccountLinkModal(false);
    
    // アカウントリンク後はページをリロードしてユーザー情報を更新
    console.log('✅ アカウントリンク成功: ページをリロードします');
    window.location.reload();
  };

  const handlePaymentSuccess = () => {
    console.log('🔍 決済成功');
    setShowPaymentModal(false);
    // 決済成功後、プランが自動的に更新される（Firestore購読により）
  };

  const handlePurchase = (plan: 'standard' | 'pro' | 'pro_max') => {
    setSelectedPlan(plan);
    if (!user) {
      // 未ログインの場合はログイン画面を表示
      setShowLoginModal(true);
      return;
    }
    // ログイン済みの場合は決済画面を表示
    setShowPaymentModal(true);
  };

  const handleShowTermsOfService = () => {
    setShowTermsOfService(true);
  };

  const handleShowPrivacyPolicy = () => {
    setShowPrivacyPolicy(true);
  };

  const handleShowCommercialAct = () => {
    setShowCommercialAct(true);
  };

  const handleImageUpload = (base64Image: string, mimeType: string) => {
    console.log('Image uploaded, switching to template selection mode');
    setOriginalImage({ data: base64Image, mimeType });
    setGeneratedImage(null);
    setCurrentStep('template');
    setError(null);
  };

  // ギャラリーに画像を保存
  const saveToGallery = (generatedImage: string, originalImage: string, prompt: string, settings?: any) => {
    // ログインユーザーの場合はFirebaseに保存、未ログインユーザーの場合はローカルストレージに保存

    // 生成画像のバリデーション
    if (!generatedImage || generatedImage.trim() === '') {
      console.warn('⚠️ Generated image is empty, skipping gallery save');
      return;
    }

    // base64データの場合のバリデーション
    if (generatedImage.startsWith('data:image/')) {
      if (generatedImage.length < 1000) {
        console.warn('⚠️ Generated image base64 is too short, skipping gallery save:', generatedImage.length);
        return;
      }
      const base64Data = generatedImage.split(',')[1];
      if (!base64Data || base64Data.length < 1000) {
        console.warn('⚠️ Generated image base64 data is invalid, skipping gallery save');
        return;
      }
    }

    // URLの場合のバリデーション
    if (generatedImage.startsWith('http')) {
      if (!generatedImage.includes('firebasestorage.googleapis.com')) {
        console.warn('⚠️ Generated image URL is invalid, skipping gallery save:', generatedImage);
        return;
      }
    }

    console.log('✅ Saving valid image to gallery:', generatedImage.substring(0, 50) + '...');

    const newImage = {
      id: Date.now().toString(),
      originalImageUrl: originalImage.startsWith('http') ? originalImage : null, // URLのみ保存
      generatedImageUrl: generatedImage.startsWith('http') ? generatedImage : null, // URLのみ保存
      createdAt: new Date(),
      prompt,
      settings: settings || null, // 再生成用の設定情報
      hasImageData: false // Firebase Storage URLのみなのでbase64データではない
    };

    try {
      // Firebase Storage URLのみを保存（base64データは保存しない）
      console.log('✅ Image saved to Firebase Storage successfully');
    } catch (error) {
      console.error('ギャラリー保存エラー:', error);
    }
  };

  const handleGenerate = async (customOutfitImageParam: { data: string; mimeType: string; } | null, customBackgroundImageParam: { data: string; mimeType: string; } | null) => {
    if (!originalImage) return;

    // 既に生成中の場合は早期リターン（重複リクエストを防ぐ）
    if (isGenerating) {
      console.warn('⚠️ 既に生成中のため、新しい生成リクエストを無視しました');
      return;
    }

    // 新しいリクエストIDを生成（前回のリクエストの結果を無視するため）
    const generationId = ++currentGenerationIdRef.current;
    console.log('🔍 新しい生成リクエスト開始:', { generationId });

    // 無料ユーザーの場合、3枚の制限をチェック（Firebaseで管理）
    if (!isPaidUser && getCreditsRest() <= 0) {
      setError('無料体験は3枚までです。有料プランにアップグレードしてください。');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);
    setCurrentStep('generate');
    setGenerationProgress(0);
    setGenerationStep('画像を分析中...');
    
    // 生成中状態を確実に表示するため、少し遅延を追加
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // 最適化されたプロンプトを生成（短く、具体的に）
      let finalPrompt = "被写体はアップロード画像の同一人物。構図は顔と腰の位置までの上半身を中心とし、撮影はプロのポートレート写真のクオリティで行う。カメラは焦点距離85mm相当、F2.8で撮影したような自然なボケ感。ライティングは柔らかいキーライトを45度から当て、補助光で影を整え、瞳にキャッチライトを入れる。メイクは自然で血色を残し、肌は健康的なツヤ、目元は印象的にしつつナチュラル、唇は自然な色でツヤ感を少し。小物は最小限で、主役は被写体本人。仕上げは過度な美肌加工や歪みを避け、自然で信頼感のある宣材写真にする。";

      // 1. 衣装設定
      let selectedOutfit;
      let outfitText = '';
      
      selectedOutfit = OUTFIT_OPTIONS.find(o => o.id === selectedOutfitId);
      console.log('衣装選択デバッグ:', {
        selectedOutfitId,
        selectedOutfit,
        customOutfitText,
        hasPrompt: !!selectedOutfit?.prompt,
        hasCustomOutfitImage: !!customOutfitImageParam
      });
      
      if (customOutfitImageParam) {
        outfitText = " 衣装: 添付画像のスタイル";
      } else if (selectedOutfit) {
        if (selectedOutfit.id === 'custom_text' && customOutfitText) {
          outfitText = ` 衣装: ${customOutfitText}`;
        } else if (selectedOutfit.prompt) {
          // 衣装プロンプトを簡潔に要約
          if (selectedOutfit.prompt.includes('ネイビー')) {
            outfitText = " 衣装: ネイビーのビジネススーツ、白いシャツ";
          } else if (selectedOutfit.prompt.includes('ブラック') && selectedOutfit.prompt.includes('スーツ')) {
            outfitText = " 衣装: ブラックのビジネススーツ、白いシャツ";
          } else if (selectedOutfit.prompt.includes('ライトグレー')) {
            outfitText = " 衣装: ライトグレーのビジネススーツ、白いシャツ";
          } else if (selectedOutfit.prompt.includes('タートルネック')) {
            outfitText = " 衣装: 黒のタートルネック";
          } else if (selectedOutfit.prompt.includes('カジュアルシャツ')) {
            outfitText = " 衣装: 白いカジュアルシャツ";
          } else if (selectedOutfit.prompt.includes('アロハシャツ')) {
            outfitText = " 衣装: アロハシャツ";
          } else {
            outfitText = " 衣装: ビジネススーツ";
          }
        }
      }
      
      finalPrompt += outfitText;

      // 2. 背景設定（簡潔に）
      if (backgroundOption === 'custom_template') {
        if (customBackground) {
          finalPrompt += ` 背景: ${customBackground}`;
        } else {
          finalPrompt += " 背景: 白いスタジオ";
        }
      } else if (backgroundOption === 'custom_image') {
        if (customBackgroundImageParam) {
          finalPrompt += " 背景: 添付画像の環境を参考";
        } else {
          finalPrompt += " 背景: 白いスタジオ";
        }
      } else {
        const template = getTemplateById(backgroundOption);
        if (template && template.prompt) {
          // 背景プロンプトを簡潔に要約
          if (template.prompt.includes('白いスタジオ')) {
            finalPrompt += " 背景: 白いスタジオ";
          } else if (template.prompt.includes('グレーのスタジオ')) {
            finalPrompt += " 背景: グレーのスタジオ";
          } else if (template.prompt.includes('ダークグレー')) {
            finalPrompt += " 背景: ダークグレーのスタジオ";
          } else if (template.prompt.includes('ライトパープル')) {
            finalPrompt += " 背景: ライトパープルのスタジオ";
          } else if (template.prompt.includes('ライトピンク')) {
            finalPrompt += " 背景: ライトピンクのスタジオ";
          } else if (template.prompt.includes('ライトブルー')) {
            finalPrompt += " 背景: ライトブルーのスタジオ";
          } else if (template.prompt.includes('木目')) {
            finalPrompt += " 背景: 白い木目";
          } else if (template.prompt.includes('美容室')) {
            finalPrompt += " 背景: 美容室風";
          } else if (template.prompt.includes('カフェ')) {
            finalPrompt += " 背景: カフェ風";
          } else if (template.prompt.includes('コンサート')) {
            finalPrompt += " 背景: コンサートホール風";
          } else if (template.prompt.includes('ビーチ')) {
            finalPrompt += " 背景: ビーチ風";
          } else if (template.prompt.includes('都会')) {
            finalPrompt += " 背景: 都会風";
          } else if (template.prompt.includes('風船')) {
            finalPrompt += " 背景: 風船で溢れかえっている";
          } else {
            finalPrompt += " 背景: プロフェッショナルなスタジオ";
          }
        } else {
          finalPrompt += " 背景: 白いスタジオ";
        }
      }

      // 3. ポーズ設定
      console.log('ポーズ設定デバッグ:', {
        selectedPoseId,
        poseOptionsLength: POSE_OPTIONS.length,
        availablePoseIds: POSE_OPTIONS.map(p => p.id)
      });
      
      const selectedPose = POSE_OPTIONS.find(p => p.id === selectedPoseId);
      console.log('選択されたポーズ:', selectedPose);
      
      if (selectedPose) {
        finalPrompt += ` ${selectedPose.prompt}`;
        console.log('ポーズプロンプト追加:', selectedPose.prompt);
      } else {
        console.log('ポーズが見つかりません:', selectedPoseId);
      }
      
      // デバッグ用：最終プロンプトをコンソールに出力
    console.log('有料版 - プロンプト生成デバッグ:', {
      backgroundOption,
      hasCustomBackgroundImage: !!customBackgroundImageParam,
      hasCustomOutfitImage: !!customOutfitImageParam,
      finalPromptLength: finalPrompt.length
    });
      console.log('有料版 - 選択された背景:', backgroundOption);
      console.log('有料版 - テンプレート詳細:', getTemplateById(backgroundOption));
      console.log('有料版 - API呼び出し前の状態:', {
        originalImageExists: !!originalImage,
        originalImageMimeType: originalImage?.mimeType,
        promptLength: finalPrompt.length,
        customOutfitImage: customOutfitImage
      });
      
      // プロンプトを保存して表示用に使用
      setCurrentPrompt(finalPrompt);

      // すべてのユーザー（匿名含む）でクレジット制限をチェック
      if (user) {
        // 無料ユーザー（匿名含む）のクレジット制限チェック
        if (!isPaidUser) {
          const creditsRest = getCreditsRest();
          console.log('🔍 無料ユーザークレジットチェック:', {
            creditsRest,
            limit: 3,
            userPlan: userPlan?.plan
          });
          
          if (creditsRest <= 0) {
            setError('無料体験の3回の制限に達しました。有料プランにアップグレードしてください。');
            setCurrentStep('template');
            return;
          }
        } else {
          // 有料ユーザーのクレジットチェック
          try {
            const { checkCredits } = await import('./services/firebase');
            const creditCheck = await checkCredits(user.uid);
            
            if (!creditCheck.hasCredits) {
              setError(`クレジットが不足しています。残りクレジット: ${creditCheck.remainingCredits}。他のデバイスで既に使用された可能性があります。`);
              setCurrentStep('template');
              return;
            }
            
            console.log('✅ Credit check passed:', {
              remainingCredits: creditCheck.remainingCredits,
              plan: creditCheck.plan?.plan
            });
          } catch (error: any) {
            console.error('❌ Credit check failed:', error);
            setError('クレジットの確認に失敗しました。しばらく待ってから再度お試しください。');
            setCurrentStep('template');
            return;
          }
        }
      }

      // モバイル環境でのタイムアウトを長めに設定
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const timeoutMs = isMobile ? 90000 : 60000; // モバイル: 90秒、PC: 60秒
      
      console.log('🔍 画像生成開始:', {
        isMobile,
        timeoutMs,
        hasOriginalImage: !!originalImage,
        promptLength: finalPrompt.length
      });

      // ネットワーク接続をチェック（スマホ版で重要）
      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        throw new Error('ネットワーク接続がありません。接続を確認してから再度お試しください。');
      }

      const result = await generateHeadshot(
        originalImage.data,
        originalImage.mimeType,
        finalPrompt,
        customOutfitImageParam, // 衣装画像
        customBackgroundImageParam // 背景画像
      );
      
      // リクエストIDをチェック：このリクエストが最新でない場合は結果を無視
      if (generationId !== currentGenerationIdRef.current) {
        console.warn('⚠️ 古いリクエストの結果を無視します:', {
          generationId,
          currentGenerationId: currentGenerationIdRef.current
        });
        return; // 古いリクエストの結果は無視
      }
      
      console.log('有料版 - 画像生成結果:', {
        result: result ? '画像データあり' : '画像データなし',
        resultLength: result ? result.length : 0,
        resultType: typeof result,
        resultPreview: result ? result.substring(0, 100) + '...' : 'null',
        hasCustomOutfitImage: !!customOutfitImageParam,
        hasCustomBackgroundImage: !!customBackgroundImageParam,
        generationId
      });
      
      setGeneratedImage(result);
      setCurrentStep('result');
      console.log('🔍 写真生成成功: result画面に遷移', {
        hasResult: !!result,
        resultType: typeof result,
        currentStep: 'result',
        isGenerating: false
      });
      
      // スマホでのスクロール位置を固定（画像生成完了後）
      setTimeout(() => {
        const scrollTop = Math.max(0, window.pageYOffset || document.documentElement.scrollTop);
        if (scrollTop > 0) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      
      // 画像生成が成功したか確認
      if (!result) {
        throw new Error('画像生成に失敗しました（結果がnullです）');
      }
      
      // imageUrlとimageIdを関数スコープで宣言
      let imageUrl: string | null = null;
      let imageId: string | null = null;
      
      // クレジット使用量を更新し、生成された画像をDBに保存（ログインユーザーは全てFirebase Storageに保存）
      if (user) {
        try {
          const template = getTemplateById(backgroundOption);
          const selectedOutfit = OUTFIT_OPTIONS.find(o => o.id === selectedOutfitId);
          const selectedPose = POSE_OPTIONS.find(p => p.id === selectedPoseId);
          
          console.log('📤 Storageへのアップロード開始...');
          
          // 再生成用の設定情報を準備（Firestore保存用）
          const regenerationSettings = {
            backgroundOption,
            selectedOutfitId,
            selectedPoseId,
            customBackground,
            customOutfitText,
            originalImageMimeType: originalImage.mimeType
            // originalImageDataは除外（Firestoreの制限を回避）
          };
          
          console.log('📋 Firestore保存用設定情報:', {
            ...regenerationSettings,
            settingsSize: JSON.stringify(regenerationSettings).length
          });
          
          console.log('🔍 画像生成結果の詳細:', {
            hasResult: !!result,
            resultType: typeof result,
            resultLength: result ? result.length : 0,
            resultPreview: result ? result.substring(0, 100) + '...' : 'none'
          });
          
          console.log('🔍 imageUrl変数宣言完了:', {
            imageUrl: imageUrl,
            imageId: imageId,
            imageUrlType: typeof imageUrl,
            imageIdType: typeof imageId
          });
          
          // saveResultを外側で宣言
          let saveResult: any = null;
          
          try {
            console.log('🚀 updateCreditsAndSaveImage呼び出し開始:', {
              uid: user.uid,
              hasResult: !!result,
              mimeType: originalImage.mimeType,
              promptLength: finalPrompt.length,
              template: template?.id,
              outfit: selectedOutfit?.id,
              pose: selectedPose?.id,
              customBackground: backgroundOption === 'custom_template' ? customBackground : undefined,
              isPaidUser: isPaidUser
            });
            
            // 無料ユーザーの場合は保存前に低解像度（50%）に変換
            let finalImageForSave = result;
            if (!isPaidUser) {
              try {
                const { downscaleImage } = await import('./utils/watermarkUtils');
                finalImageForSave = await downscaleImage(result, 0.5);
              } catch (downscaleError) {
                console.error('❌ 保存用リサイズエラー:', downscaleError);
                finalImageForSave = result;
              }
            }
            
            saveResult = await updateCreditsAndSaveImage(
              user.uid,
              finalImageForSave,
              originalImage.mimeType,
              finalPrompt,
              template?.id,
              selectedOutfit?.id,
              selectedPose?.id,
              backgroundOption === 'custom_template' ? customBackground : undefined,
              regenerationSettings
            );
            
            console.log('✅ updateCreditsAndSaveImage完了:', {
              hasImageUrl: !!saveResult.imageUrl,
              hasImageId: !!saveResult.imageId,
              imageUrlPreview: saveResult.imageUrl ? saveResult.imageUrl.substring(0, 100) + '...' : 'none'
            });
            
            imageUrl = saveResult.imageUrl;
            imageId = saveResult.imageId;
            
            console.log('🔍 imageUrl代入完了:', {
              imageUrl: imageUrl,
              imageId: imageId,
              imageUrlType: typeof imageUrl,
              imageIdType: typeof imageId
            });
            
            if (imageUrl) {
              console.log('📸 Storage URL received:', imageUrl.substring(0, 100) + '...');
              
              // 無料ユーザーは保存済みの低解像度画像URLを参照
              if (!isPaidUser) {
                console.log('🔍 無料ユーザー: Firebase保存済みの低解像度画像URLを使用');
              }
            } else {
              console.warn('⚠️ Storage URL is null or undefined');
            }
          } catch (storageError: any) {
            console.error('❌ Storage保存エラー:', storageError);
            console.warn('⚠️ Storage保存に失敗しましたが、画像は表示されます');
            // Storage保存に失敗しても処理を継続（画像は表示される）
            imageUrl = null;
            imageId = null;
            console.log('🔍 Storage保存エラー後の状態:', {
              imageUrl: imageUrl,
              imageId: imageId,
              hasResult: !!result,
              resultType: typeof result
            });
          }
          
          // クレジット更新はatomicConsumeCreditで既に処理済み
          console.log('💳 クレジット更新はatomicConsumeCreditで既に処理済み');
          
          console.log('✅ クレジット使用量と画像をDBに保存しました');
        } catch (error: any) {
          console.error('❌ クレジット更新・画像保存エラー:', error);
          const errorMsg = error?.message || String(error);
          
          // クレジット不足のエラーの場合は特別な処理
          if (errorMsg.includes('クレジットが不足しています')) {
            setError('クレジットが不足しています。他のデバイスで既に使用された可能性があります。プランをアップグレードするか、しばらく待ってから再度お試しください。');
            setCurrentStep('template'); // テンプレート選択画面に戻す
            return;
          }
          
          if (errorMsg?.includes?.('CORS') || errorMsg?.includes?.('preflight')) {
            setError('画像の保存時にネットワークエラーが発生しました。ブラウザをリロードして再度お試しください。');
          } else if (errorMsg?.includes?.('permission') || errorMsg?.includes?.('403')) {
            setError('画像の保存権限がありません。ログインし直してください。');
          } else {
            setError('画像の保存に失敗しました。画像は表示されていますが、ギャラリーには保存されていません。');
          }
          // エラーでも画像は表示する（保存失敗のみ）
        }
      }
      
      // ギャラリーに保存（ログインユーザーは全てFirebase Storageに保存）
      console.log('🔍 ギャラリー保存前の状態確認:', {
        isPaidUser,
        hasImageUrl: !!imageUrl,
        imageUrlType: typeof imageUrl,
        imageUrlValue: imageUrl,
        hasResult: !!result,
        resultType: typeof result,
        userEmail: user?.email,
        userPlan: userPlan?.plan
      });
      
      if (imageUrl) {
        // Firebase Storage URLが取得できた場合
        const galleryImageData = imageUrl;
        const gallerySettings = {
          backgroundOption,
          selectedOutfitId,
          selectedPoseId,
          customBackground,
          customOutfitText,
          originalImageData: originalImage.data, // LocalStorageには保存可能
          originalImageMimeType: originalImage.mimeType
        };
        
        console.log('🔍 saveToGallery呼び出し開始:', {
          galleryImageDataType: typeof galleryImageData,
          galleryImageDataPreview: galleryImageData.substring(0, 100) + '...',
          finalPrompt: finalPrompt.substring(0, 50) + '...',
          settingsKeys: Object.keys(gallerySettings)
        });
        
        saveToGallery(galleryImageData, '', finalPrompt, gallerySettings);
        
        console.log('✅ ギャラリーに保存しました（Firebase Storage）:', {
          imageId,
          isPaidUser,
          hasImageUrl: !!imageUrl,
          imageUrlPreview: imageUrl ? imageUrl.substring(0, 100) + '...' : 'none',
          galleryImageData: galleryImageData.substring(0, 50) + '...'
        });
      } else {
        // Firebase Storage URLが取得できなかった場合
        console.error('❌ Firebase Storage URLが取得できませんでした。ギャラリーには保存されません。', {
          isPaidUser,
          userEmail: user?.email,
          userPlan: userPlan?.plan,
          hasResult: !!result,
          saveResultKeys: saveResult ? Object.keys(saveResult) : 'saveResult is null'
        });
      }
      
      // すべてのユーザーはFirebaseで管理
      if (user) {
        // updateCreditsAndSaveImageで既にクレジット使用量は更新済み
        console.log('✅ クレジット使用量は既に更新済み');
        console.log('🔍 写真生成後の状態:', {
          user: user ? { uid: user.uid } : null,
          isPaidUser,
          userPlan,
          getFreeTrialUsage: getCreditsRest()
        });
        // AuthContextの状態を同期
        await forceSyncCredits();
        console.log('🔍 forceSyncCredits後の状態:', {
          user: user ? { uid: user.uid } : null,
          isPaidUser,
          userPlan,
          getFreeTrialUsage: getCreditsRest()
        });
        
        // Firebase Storageに保存された画像のURLをギャラリーに保存
        if (result && typeof result === 'object' && result !== null && 'imageUrl' in result) {
          const imageUrl = (result as any).imageUrl;
          if (imageUrl) {
          const gallerySettings = {
            backgroundOption,
            selectedOutfitId,
            selectedPoseId,
            customBackground,
            customOutfitText,
            originalImageData: originalImage.data,
            originalImageMimeType: originalImage.mimeType
          };
          
          saveToGallery(imageUrl, originalImage.data, finalPrompt, gallerySettings);
          console.log('✅ Firebase Storageの画像URLをギャラリーに保存');
          }
        }
      } else {
        // このケースは発生しないはず（すべてのユーザーは匿名ログイン済み）
        console.error('❌ 予期しないエラー: ユーザーが存在しません');
        throw new Error('ユーザーが存在しません。匿名ログインを実行してください。');
      }
    } catch (e: any) {
      // リクエストIDをチェック：このリクエストが最新でない場合はエラーを無視
      if (generationId !== currentGenerationIdRef.current) {
        console.warn('⚠️ 古いリクエストのエラーを無視します:', {
          generationId,
          currentGenerationId: currentGenerationIdRef.current,
          error: e.message
        });
        return; // 古いリクエストのエラーは無視
      }
      
      console.error('有料版画像生成エラー:', e);
      console.error('エラー詳細:', {
        message: e.message,
        code: e.code,
        details: e.details,
        stack: e.stack,
        name: e.name,
        generationId
      });

      // Firebase Functionsからのエラーメッセージを解析
      let displayMessage = e.message || '画像生成中にエラーが発生しました。';
      let suggestion = '';

      // タイムアウトエラーの特別処理
      if (e.message?.includes('タイムアウト') || e.message?.includes('timeout') || e.code === 'deadline-exceeded') {
        displayMessage = '⏱️ 画像生成に時間がかかりすぎました。';
        suggestion = '\n\n💡 ヒント: ネットワーク接続を確認し、しばらく待ってから再度お試しください。モバイル環境では時間がかかる場合があります。';
      }

      // エラー詳細から理由と提案を取得
      if (e.details) {
        const reason = e.details.reason;
        const errorSuggestion = e.details.suggestion;

        if (reason === 'timeout') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'invalid_prompt') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'generation_failed') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'file_too_large') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'api_limit') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (errorSuggestion && !suggestion) {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        }
      }

      // エラーコードに基づく追加処理
      if (e.code === 'deadline-exceeded' && !displayMessage.includes('タイムアウト')) {
        displayMessage = '⏱️ ' + displayMessage;
      } else if (e.code === 'invalid-argument') {
        displayMessage = '⚠️ ' + displayMessage;
      } else if (e.code === 'resource-exhausted') {
        displayMessage = '🚫 ' + displayMessage;
      } else if (e.code === 'internal') {
        displayMessage = '❌ ' + displayMessage;
      }

      // ネットワークエラーの特別処理
      if (e.message?.includes('ネットワーク') || 
          e.message?.includes('接続') || 
          e.message?.includes('Failed to fetch') ||
          e.message?.includes('NetworkError') ||
          e.code === 'unavailable') {
        displayMessage = '🌐 ネットワークエラーが発生しました。';
        suggestion = '\n\n💡 ヒント: ネットワーク接続を確認し、しばらく待ってから再度お試しください。';
      }

      setError(displayMessage + suggestion);
      setCurrentStep('template'); // エラー時はテンプレート選択に戻す
      console.log('🔍 写真生成エラー: template画面に戻る', {
        error: e.message,
        currentStep: 'template',
        isGenerating: false
      });
    } finally {
      // リクエストIDをチェック：このリクエストが最新でない場合は状態をリセットしない
      if (generationId !== currentGenerationIdRef.current) {
        console.warn('⚠️ 古いリクエストのfinallyブロックをスキップします:', {
          generationId,
          currentGenerationId: currentGenerationIdRef.current
        });
        return; // 古いリクエストのfinallyは実行しない
      }
      
      // 確実に生成状態をリセット（スマホ版でも確実に実行されるように）
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStep('');
      console.log('🔍 handleGenerate finally: isGenerating = false', { generationId });
    }
  };

  const handleReset = () => {
    console.log('🔍 handleResetが呼ばれました');
    setOriginalImage(null);
    setGeneratedImage(null);
    setError(null);
    setIsGenerating(false);
    setGenerationProgress(0);
    setGenerationStep('');
    setCurrentStep('upload');
    
    // Reset all options to default
    setSelectedOutfitId('suit_blue');
    setCustomOutfitText('');
    setBackgroundOption('default');
    setCustomBackground('');
    setSelectedPoseId('front');
    setInheritedStyle(null);
    clearInheritedStyle();
    
    // リセット時はLandingPageに戻らず、アップロード画面に戻る
    console.log('🔍 handleReset: アップロード画面に戻ります（LandingPageには戻りません）');
  };

  // プラン情報セクションにスクロールする関数
  const scrollToPlanInfo = () => {
    // アプリ画面にいる場合はLandingPageに戻る
    if (!showLandingPage) {
      setShowLandingPage(true);
      
      // Reactのレンダリング完了を待ってからスクロール
      setTimeout(() => {
        const planInfoElement = document.getElementById('plan-info-section');
        if (planInfoElement) {
          const elementPosition = planInfoElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - 10,
            behavior: 'smooth'
          });
        }
      }, 300);
      return;
    }
    
    // LandingPageにいる場合はプラン情報セクションにスクロール
    const planInfoElement = document.getElementById('plan-info-section');
    if (planInfoElement) {
      const elementPosition = planInfoElement.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - 10,
        behavior: 'smooth'
      });
    }
  };

  return (
      <Router>
        <Routes>
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/musician" element={<MusicianPage />} />
          <Route path="/portrait" element={<PortraitPage />} />
          <Route path="*" element={
          showLandingPage ? (
            <div className="min-h-screen font-sans antialiased">
              <Header 
                onShowGallery={() => setShowGalleryModal(true)} 
                onGoHome={handleGoHome}
                showTrialInfo={!isPaidUser}
                trialRemaining={getCreditsRest()}
                userPlan={userPlan}
              />
              <LandingPage 
                onStartTrial={handleStartTrial} 
                onShowTermsOfService={handleShowTermsOfService}
                onShowPrivacyPolicy={handleShowPrivacyPolicy}
                onShowCommercialAct={handleShowCommercialAct}
                onPurchase={handlePurchase}
                onStartApp={handleStartApp}
                onShowGallery={() => setShowGalleryModal(true)}
              />
            </div>
          ) : (
            <div className="bg-slate-50 min-h-screen font-sans antialiased">
              <Header 
                onShowGallery={() => setShowGalleryModal(true)} 
                onGoHome={handleGoHome}
                showTrialInfo={!isPaidUser}
                trialRemaining={getCreditsRest()}
                userPlan={userPlan}
              />
              <main className="container mx-auto px-4 md:px-8 pt-20 pb-6 sm:pb-8 md:pb-12">
                {/* ステップ表示 */}
                <div className="mb-8">
                  <div className="flex items-center justify-center space-x-4">
                    {[
                      { step: 'upload', label: '写真を選ぶ' },
                      { step: 'template', label: 'テンプレート選択' },
                      { step: 'generate', label: '生成中' },
                      { step: 'result', label: '結果' }
                    ].map((item, index) => (
                      <div key={item.step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          currentStep === item.step
                            ? 'bg-blue-600 text-white'
                            : index < ['upload', 'template', 'generate', 'result'].indexOf(currentStep)
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="ml-2 text-sm font-medium text-slate-600">
                          {item.label}
                        </span>
                        {index < 3 && (
                          <svg className="w-4 h-4 text-slate-400 mx-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ステップ別コンテンツ */}
                {inheritedStyle && (currentStep === 'upload' || currentStep === 'template') && (
                  <div className="max-w-2xl mx-auto mb-6">
                    <div className="flex items-start gap-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left">
                      {inheritedStyle.imageUrl ? (
                        <img
                          src={inheritedStyle.imageUrl}
                          alt="引き継いだスタイル"
                          className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                          <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
                            <circle cx="12" cy="12" r="3.5" strokeWidth={2} />
                          </svg>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-blue-900">ショーケースのスタイルを引き継ぎました</p>
                        <p className="mt-1 text-sm text-blue-800">
                          自分の写真をアップロードすると、同じ雰囲気の宣材写真を作れます。背景・衣装・ポーズはあらかじめ設定済みです。
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setInheritedStyle(null);
                          clearInheritedStyle();
                        }}
                        className="flex-shrink-0 rounded-lg px-2 py-1 text-sm text-blue-700 hover:bg-blue-100"
                      >
                        解除
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 'upload' && (
                  <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">
                      ステップ1: 写真を選ぶ
                    </h2>
                    <p className="text-slate-600 mb-8">
                      顔がはっきり写った正面の写真をアップロードしてください
                    </p>
                    <ImageUploader 
                      onImageUpload={handleImageUpload} 
                      freeTrialUsed={getCreditsRest()} 
                      scrollToPlanInfo={scrollToPlanInfo}
                      userPlan={userPlan}
                    />
                  </div>
                )}

                {currentStep === 'template' && (
                  <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-slate-900">
                        ステップ2: テンプレート選択
                      </h2>
                      <button
                        onClick={() => setCurrentStep('upload')}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        戻る
                      </button>
                    </div>
                    <p className="text-slate-600 mb-8">
                      お好みのテンプレートと衣装を選択してください
                    </p>
                    <ControlPanel
                      onGenerate={(customOutfit, customBackground) => handleGenerate(customOutfit, customBackground)}
                      onReset={handleReset}
                      isLoading={isGenerating}
                      error={error}
                      isPaidUser={isPaidUser}
                      isAnonymous={false}
                      userPlan={userPlan}
                      scrollToPlanInfo={scrollToPlanInfo}
                      selectedOutfitId={selectedOutfitId}
                      onOutfitIdChange={setSelectedOutfitId}
                      customOutfitText={customOutfitText}
                      onCustomOutfitTextChange={setCustomOutfitText}
                      customOutfitImage={customOutfitImage}
                      onCustomOutfitImageChange={setCustomOutfitImage}
                      backgroundOption={backgroundOption}
                      onBackgroundOptionChange={setBackgroundOption}
                      customBackground={customBackground}
                      onCustomBackgroundChange={setCustomBackground}
                      customBackgroundImage={customBackgroundImage}
                      onCustomBackgroundImageChange={setCustomBackgroundImage}
                      selectedPoseId={selectedPoseId}
                      onPoseIdChange={setSelectedPoseId}
                    />
                  </div>
                )}

                {currentStep === 'generate' && (
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">
                      生成中...
                    </h2>
                    <div className="bg-slate-100 rounded-lg p-8">
                      <div className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse">
                        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      
                      {/* 進行状況表示 */}
                      <div className="mb-6">
                        <p className="text-lg font-semibold text-slate-700 mb-2">{generationStep || 'AIが宣材写真を生成しています'}</p>
                        <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${generationProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-slate-500">{generationProgress}%</p>
                      </div>
                      
                      {/* プロンプト表示エリア */}
                      {currentPrompt && (
                        <div className="mt-6 bg-white rounded-lg p-4 border">
                          <h4 className="text-sm font-medium text-slate-700 mb-2">送信中のプロンプト:</h4>
                          <div className="bg-slate-50 rounded p-3 max-h-32 overflow-y-auto">
                            <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">
                              {currentPrompt}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {currentStep === 'result' && originalImage && (
                  <div className="w-full">
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">
                      生成完了！
                    </h2>
                    <p className="text-slate-600 mb-8">
                      生成された宣材写真をご確認ください
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                      {(() => {
                        console.log('🔍 ImageViewer props debug:', {
                          isPaidUser,
                          userPlan: userPlan?.plan,
                          userEmail: user?.email,
                          creditsRest: userPlan?.creditsRest,
                          creditsUsed: userPlan?.creditsUsed
                        });
                        return null;
                      })()}
                      <ImageViewer
                        originalImage={originalImage.data}
                        generatedImage={generatedImage}
                        isLoading={isGenerating}
                        isPaidUser={isPaidUser}
                        currentPrompt={currentPrompt}
                        userId={user?.uid}
                      />
                      <div className="bg-white rounded-lg p-6 border border-slate-200">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4">次のアクション</h3>
                        <div className="space-y-3">
                          <div className="flex gap-3 flex-wrap">
                            <button
                              onClick={() => {
                                resetSettings();
                                setCurrentStep('upload');
                              }}
                              className="flex-1 min-w-[140px] bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              新しい写真を作成
                            </button>
                            <button
                              onClick={() => {
                                window.location.href = '/musician';
                              }}
                              className="flex-1 min-w-[140px] bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors"
                            >
                              音楽家版を作る 🎻
                            </button>
                            <button
                              onClick={() => {
                                window.location.href = '/portrait';
                              }}
                              className="flex-1 min-w-[140px] bg-teal-700 text-white py-3 px-4 rounded-lg hover:bg-teal-800 transition-colors"
                            >
                              ポートレートモード
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              // 再生成前に状態をリセット（前回のリクエストを無視するため、新しいIDを生成）
                              console.log('🔄 再生成ボタンクリック: 前回のリクエストを無効化');
                              currentGenerationIdRef.current++; // 前回のリクエストIDを無効化
                              setIsGenerating(false);
                              setError(null);
                              setGenerationProgress(0);
                              setGenerationStep('');
                              setCurrentStep('template');
                            }}
                            className="w-full bg-slate-200 text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-300 transition-colors"
                          >
                            設定を変更して再生成
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </main>
            </div>
          )
        } />
      </Routes>
      
      {/* モーダル群 */}
      <TrialModal
                isOpen={showTrialModal}
                onClose={() => setShowTrialModal(false)}
                onUpgrade={handleUpgrade}
                userPlan={userPlan}
                onTrialUsed={updateFreeTrialUsage}
                isPaidUser={isPaidUser}
                scrollToPlanInfo={scrollToPlanInfo}
              />
              <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onLoginSuccess={handleLoginSuccess}
              />
      <PlanSelectionModal
        isOpen={showPlanSelectionModal}
        onClose={() => setShowPlanSelectionModal(false)}
        onSelectPlan={handlePlanSelect}
        currentPlan={(() => {
          console.log('🔍 App.tsx - PlanSelectionModal用のプラン情報:', {
            userPlan,
            userPlanPlan: userPlan?.plan,
            userPlanPlanType: typeof userPlan?.plan,
            finalPlan: userPlan?.plan || null
          });
          
          // 無料プランの場合はnullを返す（PlanSelectionModalは有料プランのみ対応）
          if (!userPlan || userPlan.plan === 'free') {
            console.log('🔍 無料プランのため、currentPlanはnull');
            return null;
          }
          
          console.log('🔍 有料プランのため、currentPlan:', userPlan.plan);
          return userPlan.plan as 'standard' | 'pro' | 'pro_max';
        })()}
              />
              <AccountLinkModal
                isOpen={showAccountLinkModal}
                onClose={() => setShowAccountLinkModal(false)}
                onLinkSuccess={handleAccountLinkSuccess}
              />
              <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onPaymentSuccess={handlePaymentSuccess}
                plan={selectedPlan}
              />
              <TermsOfService
                isOpen={showTermsOfService}
                onClose={() => setShowTermsOfService(false)}
              />
              <PrivacyPolicy
                isOpen={showPrivacyPolicy}
                onClose={() => setShowPrivacyPolicy(false)}
              />
              <CommercialTransactionAct
                isOpen={showCommercialAct}
                onClose={() => setShowCommercialAct(false)}
              />
              <GalleryModal
                isOpen={showGalleryModal}
                onClose={() => setShowGalleryModal(false)}
              />
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </AuthProvider>
  );
}

// エラーバウンダリーコンポーネント
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'red' }}>
          <h2>エラーが発生しました</h2>
          <p>{this.state.error?.message}</p>
          <pre>{this.state.error?.stack}</pre>
        </div>
      );
    }

    return this.props.children;
  }
}

export default App;
